/**
 * Color mapping for the office's default tags.
 *
 * The backend seeds these as `is_default_tag=True` rows on every office
 * (see `clinics.signals.create_default_staging_groups` and migration
 * `0084_seed_new_default_tags`). The names below MUST stay in sync with
 * the seeded names — keyed by tag name rather than id since ids vary
 * per-office.
 *
 * Custom-created tags fall through to `undefined`, which renders as
 * AntD's default chip styling.
 */

// AntD preset names render SOFT tags; a custom hex renders a SOLID filled tag.
// That distinction is used deliberately: Urgent is the custom hex (the one
// full-opacity tag in the system), Requested is the soft `volcano` preset
// (dark burnt-orange text), matching STATUS_PALETTE.
const TAG_COLOR_BY_NAME = {
  "Breakdown - Verified":            "green",      // soft green
  "Breakdown - Requested":           "volcano",    // soft, dark burnt-orange text
  "Breakdown - Requested - Urgent":  "#cf1322",    // SOLID red, white text
  "Details Needed by Smilepass":     "gold",       // soft yellow
  // Estimate flow mirrors the breakdown palette so the patient row reads
  // uniformly across the two parallel agent workflows.
  "New Estimate Available":          "green",
  "Estimate - Requested":            "volcano",
  "Estimate - Requested - Urgent":   "#cf1322",
  // Claim follow-up: the third request type, same three states.
  "Claim Follow-up - Done":                "green",
  "Claim Follow-up - Requested":           "volcano",
  "Claim Follow-up - Requested - Urgent":  "#cf1322",
};

// Full palette triples (background / border / text) for places that
// render tags as plain `<div>`s rather than AntD `<Tag>` (e.g., the
// patient-list tag-filter bar at the top of TreatmentPlans). Triples
// match AntD's standard 1-3-7 palette tokens for consistency.
// ---------------------------------------------------------------------------
// THE status palette — the platform's single vocabulary for request/outcome
// state, exported so every surface (board tags, FTC chips + priority filter,
// Insurance Verification statuses, modals) draws from ONE definition.
//
// Six semantics, six hues, learned from the board (the oldest surface):
//   requested  red      a human asked
//   urgent     dark red a human asked, sooner (text carries the word too)
//   attention  gold     something needs a human decision
//   done       green    verified / available / ready
//   auto       geekblue system-detected work (FTC only); a check scheduled for later
//   cancelled  grey     withdrawn
// and, for the carrier's answers and the office's own follow-through:
//   partial    orange   partially covered
//   negative   soft red not covered
//   office     purple   the office's own status (contact patient, appointment booked)
//
// Triples are AntD 1/4/7 tiers. If a color changes here it changes everywhere —
// that is the point. Deviating locally is how the platform ended up with
// "requested" meaning orange on one page and red on another.
// ---------------------------------------------------------------------------
export const STATUS_PALETTE = {
  // Urgent is the ONE solid-filled chip in the system — full-opacity red with
  // white text, so the escalated ask is unmistakably the loudest thing on any
  // row. Requested is soft dark burnt-orange (volcano 1/4/8): warm and serious,
  // clearly apart from both urgent's red and attention's gold.
  requested: { backgroundColor: "#fff2e8", borderColor: "#ff9c6e", color: "#ad2102" },
  urgent:    { backgroundColor: "#cf1322", borderColor: "#a8071a", color: "#ffffff" },
  attention: { backgroundColor: "#fffbe6", borderColor: "#ffd666", color: "#d48806" },
  done:      { backgroundColor: "#f6ffed", borderColor: "#95de64", color: "#389e0d" },
  auto:      { backgroundColor: "#f0f5ff", borderColor: "#adc6ff", color: "#1d39c4" },
  cancelled: { backgroundColor: "#fafafa", borderColor: "#d9d9d9", color: "#8c8c8c" },
  partial:   { backgroundColor: "#fff7e6", borderColor: "#ffd591", color: "#d46b08" },
  negative:  { backgroundColor: "#fff1f0", borderColor: "#ffa39e", color: "#cf1322" },
  office:    { backgroundColor: "#f9f0ff", borderColor: "#d3adf7", color: "#531dab" },
};

const TAG_PALETTE_BY_NAME = {
  "Breakdown - Verified": STATUS_PALETTE.done,
  "Breakdown - Requested": STATUS_PALETTE.requested,
  "Breakdown - Requested - Urgent": STATUS_PALETTE.urgent,
  "Details Needed by Smilepass": STATUS_PALETTE.attention,
  // Estimate-flow palettes mirror their Breakdown counterparts so the two
  // parallel agent workflows render identically on the patient row.
  "New Estimate Available": STATUS_PALETTE.done,
  "Estimate - Requested": STATUS_PALETTE.requested,
  "Estimate - Requested - Urgent": STATUS_PALETTE.urgent,
  "Claim Follow-up - Done": STATUS_PALETTE.done,
  "Claim Follow-up - Requested": STATUS_PALETTE.requested,
  "Claim Follow-up - Requested - Urgent": STATUS_PALETTE.urgent,
};

// ---------------------------------------------------------------------------
// Custom (user-created) tags.
//
// The 10 default tags above are name-keyed. Custom tags have arbitrary names,
// so instead of falling through to a flat gray/purple we assign each one a
// stable, distinct color derived from a hash of its name — the same name always
// maps to the same color, for every user, with no backend `color` column.
// Brand purple is intentionally omitted so custom tags don't read as default.
// Each triple is a soft light background + one-step-darker border + readable
// dark text, matching the AntD 1/4/7 token tiers used by the default tags.
// ---------------------------------------------------------------------------
const CUSTOM_TAG_PALETTES = [
  { backgroundColor: "#e6f4ff", borderColor: "#69b1ff", color: "#0958d9" }, // blue
  { backgroundColor: "#e6fffb", borderColor: "#5cdbd3", color: "#08979c" }, // cyan
  { backgroundColor: "#f6ffed", borderColor: "#95de64", color: "#389e0d" }, // green
  { backgroundColor: "#fff7e6", borderColor: "#ffc069", color: "#d46b08" }, // orange
  { backgroundColor: "#fff0f6", borderColor: "#ff85c0", color: "#c41d7f" }, // magenta
  { backgroundColor: "#f9f0ff", borderColor: "#b37feb", color: "#531dab" }, // deep purple
  { backgroundColor: "#fcffe6", borderColor: "#d3f261", color: "#7cb305" }, // lime
  { backgroundColor: "#fff2e8", borderColor: "#ff9c6e", color: "#d4380d" }, // volcano
  { backgroundColor: "#f0f5ff", borderColor: "#adc6ff", color: "#1d39c4" }, // geekblue
  { backgroundColor: "#fffbe6", borderColor: "#ffe58f", color: "#d4b106" }, // gold
  { backgroundColor: "#fff1f0", borderColor: "#ffa39e", color: "#cf1322" }, // red
  { backgroundColor: "#e6fffb", borderColor: "#87e8de", color: "#006d75" }, // teal-dark
];

// AntD preset name aligned with each palette index (for `<Tag color=…>` sites).
const CUSTOM_TAG_ANTD_COLORS = [
  "blue", "cyan", "green", "orange", "magenta", "purple",
  "lime", "volcano", "geekblue", "gold", "red", "cyan",
];

// Stable, order-independent 32-bit string hash → non-negative int.
function hashTagName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0; // keep it 32-bit
  }
  return Math.abs(hash);
}

function customPaletteIndex(name) {
  return hashTagName(name) % CUSTOM_TAG_PALETTES.length;
}

/** Lookup the AntD preset (or hex) for the named tag — used by the
 *  per-row Select's tagRender / optionRender. Default tags use the fixed
 *  map above; custom tags get a stable, hash-assigned preset. */
export function colorForTag(tagName) {
  if (!tagName) return undefined;
  if (TAG_COLOR_BY_NAME[tagName]) return TAG_COLOR_BY_NAME[tagName];
  return CUSTOM_TAG_ANTD_COLORS[customPaletteIndex(tagName)];
}

/** Lookup the {backgroundColor, borderColor, color} triple for the named
 *  tag — used by surfaces that render tags as styled `<div>`s (the filter
 *  pills at the top of TreatmentPlans, the Manage Tags list). Default tags
 *  use the fixed map above; custom tags get a stable, hash-assigned palette
 *  so each is easy to tell apart. */
export function paletteForTag(tagName) {
  if (!tagName) return undefined;
  if (TAG_PALETTE_BY_NAME[tagName]) return TAG_PALETTE_BY_NAME[tagName];
  return CUSTOM_TAG_PALETTES[customPaletteIndex(tagName)];
}

export default TAG_COLOR_BY_NAME;
